import React, { FC, useState, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { PerModal } from '../../components/custom_components/Modal'
import PerText from '../../components/custom_components/Text'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { Col, Icon, Spin, Checkbox } from 'antd'
import PerDropzone from '../../components/custom_components/DragAndDrop'
import { IsProcessingFile } from '../companySettings/cultivatingTalents/RankingEditImportExportBox'
import { ExcelManager } from '../../shared/xlsx/ExcelManager'
import { Country, DraftScoreGridInput, ScoreGoalType, ScoreGridExcelDataInput, ScoreGoalDto, BusinessUnitTiny } from '../../types'
import { importExcel } from '../../graphql/draft-score-grid/useDraftScoreGrid'
import PerButtonCircle from '../../components/custom_components/ButtonCircle'
import PerSpacer from '../../components/custom_components/Spacer'
import { useForm, Controller } from 'react-hook-form'
import { showNotification } from '../../components/custom_components/Notification/notification'

type DraftScoreGridsImportModalContentProps = {
  employeeIdsAndScoreGoals: { employeeId: number; scoreGoals?: ScoreGoalDto[] | null; hasDraft: boolean; bu: BusinessUnitTiny }[]
  scoreGoalTypes: string[]
  companyId: string
  country: Country
  setIsOpened: (value: boolean) => void
  businessUnitsOfCompany: BusinessUnitTiny[]
}

export const DraftScoreGridsImportModalContent: FC<DraftScoreGridsImportModalContentProps> = ({
  employeeIdsAndScoreGoals,
  scoreGoalTypes,
  companyId,
  country,
  setIsOpened,
  businessUnitsOfCompany,
}) => {
  const { t } = useTranslation()
  const [dropZoneError, setDropZoneError] = useState<string | undefined>(undefined)
  const [dropZoneProcessing, setDropZoneProcessing] = useState<IsProcessingFile>({
    active: false,
    isProcessing: false,
    text: undefined,
    errorMessage: undefined,
  })
  const [filename, setFilename] = useState<string | undefined>(undefined)
  const [importedData, setImportedData] = useState<ScoreGridExcelDataInput[] | undefined>(undefined)
  const [isImporting, setIsImporting] = useState<boolean>(false)
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<number[]>([])
  const processingMessageRef = useRef<HTMLDivElement>(null)

  const buForm = useForm<{ businessUnits: { id: number; name: string; value: boolean }[]; all: { name: string; value: boolean } }>({
    defaultValues: {
      businessUnits: businessUnitsOfCompany.map((bu) => {
        return { id: bu.id, name: bu.name, value: false }
      }),
      all: { name: t('draftScoreGrid.importModal.all'), value: false },
    },
    criteriaMode: 'all',
    mode: 'onChange',
  })

  const { control, getValues, setValue } = buForm

  const MAX_ALLOWED_FILE_SIZE = 1048576

  const antIcon = (
    <Icon
      type={dropZoneProcessing.isProcessing ? 'loading' : dropZoneProcessing.errorMessage ? 'exclamation' : 'check'}
      style={{
        fontSize: 20,
        color: dropZoneProcessing.isProcessing ? '#A3A9AD' : dropZoneProcessing.errorMessage ? '#FF6259' : '#FFFFFF',
        display: 'flex',
      }}
      spin={dropZoneProcessing.isProcessing ? true : false}
    />
  )

  const closeModal = () => {
    if (!isImporting) {
      setDropZoneError(undefined)
      setDropZoneProcessing({
        active: false,
      })
      setFilename(undefined)
      setImportedData(undefined)
      setIsOpened(false)
      setIsImporting(false)
    }
  }

  const deleteDroppedFile = () => {
    if (!isImporting) {
      setDropZoneError(undefined)
      setDropZoneProcessing({
        active: false,
      })
      setFilename(undefined)
      setImportedData(undefined)
      setIsImporting(false)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onDrop = (uploadedfile: any) => {
    let hasError = false
    if (!uploadedfile) {
      hasError = true
      setDropZoneError(t('draftScoreGrid.importModal.errors.noFile'))
    } else if (!uploadedfile.type) {
      hasError = true
      setDropZoneError(t('draftScoreGrid.importModal.errors.noType'))
    } else if (!uploadedfile.path) {
      hasError = true
      setDropZoneError(t('draftScoreGrid.importModal.errors.noPath'))
    } else {
      const extension = uploadedfile.path.substring(uploadedfile.path.lastIndexOf('.') + 1, uploadedfile.path.length)
      if (extension !== 'xlsx') {
        hasError = true
        setDropZoneError(t('draftScoreGrid.importModal.errors.extension'))
      } else {
        importExcelFile(uploadedfile)
      }
    }
    if (hasError) {
      setDropZoneProcessing({
        active: false,
        isProcessing: false,
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const importExcelFile = async (uploadedFile: any) => {
    let importedFileAsJson: string[]
    setImportedData(undefined)

    try {
      importedFileAsJson = await ExcelManager.getInstance().importExcelFile(uploadedFile)
    } catch (err) {
      setDropZoneProcessing({
        active: false,
        isProcessing: false,
        errorMessage: t('draftScoreGrid.importModal.errors.unknown'),
      })
      return
    }

    setDropZoneProcessing({
      active: true,
      isProcessing: true,
      text: t('draftScoreGrid.importModal.processingMessage'),
    })

    let transformedFilename = uploadedFile.path
    const indexOfSlash = uploadedFile.path.indexOf('/')
    if (indexOfSlash >= 0) {
      transformedFilename = transformedFilename.substring(indexOfSlash + 1)
    }

    setFilename(transformedFilename)

    const refactoredExcelData: ScoreGridExcelDataInput[] | string = refactorExcelData(JSON.parse(importedFileAsJson[0]))
    if (typeof refactoredExcelData === 'string') {
      setDropZoneProcessing({
        active: true,
        isProcessing: false,
        errorMessage: refactoredExcelData,
      })
      if (processingMessageRef.current) {
        processingMessageRef.current.scrollIntoView({ behavior: 'smooth' })
      }
      return
    }

    setImportedData(refactoredExcelData)
    setDropZoneProcessing({
      active: true,
      isProcessing: false,
      text: t('draftScoreGrid.importModal.successMessage'),
    })
    if (processingMessageRef.current) {
      processingMessageRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const refactorExcelData = (jsonObject: any): ScoreGridExcelDataInput[] | string => {
    const res: ScoreGridExcelDataInput[] = []
    const scoreGoals: { type: ScoreGoalType; columnName: string }[] = [
      ...scoreGoalTypes.map((type) => {
        return { type: type as ScoreGoalType, columnName: t(`draftScoreGrid.excel.fileColumnNames.scoreGoal.${type}`) }
      }),
    ]
    if (!selectedBusinessUnits.length) {
      return t('draftScoreGrid.importModal.errors.noBuSelected')
    }
    const employeeIds: number[] = employeeIdsAndScoreGoals.map((employeeData) => employeeData.employeeId)
    let employeesNumberForSelectedBUs = 0
    for (let i = 0; i < jsonObject.length; i++) {
      const headerNames: string[] = Object.keys(jsonObject[i])
      const idHeaderNames: string[] = headerNames.filter(
        (key: string) => key.toLocaleUpperCase() === t('draftScoreGrid.excel.fileColumnNames.id').toLocaleUpperCase(),
      )
      if (idHeaderNames.length !== 1) {
        return idHeaderNames.length > 1
          ? t('draftScoreGrid.importModal.errors.duplicatedColumn', { idColumnName: t('draftScoreGrid.excel.fileColumnNames.id') })
          : t('draftScoreGrid.importModal.errors.noId', { rowNo: i + 2 })
      }
      const idHeaderName = idHeaderNames[0]

      if (!jsonObject[i][idHeaderName]) {
        return t('draftScoreGrid.importModal.errors.noId', { rowNo: i + 2 })
      }
      const excelEmployeeId = jsonObject[i][idHeaderName]
      if (!employeeIds.includes(excelEmployeeId)) {
        return t('draftScoreGrid.importModal.errors.unknownEmployee', { rowNo: i + 2 })
      }
      for (let j = i + 1; j < jsonObject.length; j++) {
        if (!jsonObject[j][idHeaderName]) {
          return t('draftScoreGrid.importModal.errors.unknownEmployee', { rowNo: j + 2 })
        }
        if (excelEmployeeId === jsonObject[j][idHeaderName]) {
          return t('draftScoreGrid.importModal.errors.duplicatedId', { rowNo1: i + 2, rowNo2: j + 2 })
        }
      }
      const buOfEmployee = employeeIdsAndScoreGoals.find((e) => e.employeeId === excelEmployeeId)!.bu
      if (selectedBusinessUnits.includes(buOfEmployee.id)) {
        employeesNumberForSelectedBUs++
        let atLeastOneScoreGoal = false
        for (const scoreGoal of scoreGoals) {
          const scoreGoalHeaderName: string | undefined = headerNames.find(
            (key: string) => key.toLocaleUpperCase() === scoreGoal.columnName.toLocaleUpperCase(),
          )
          if (scoreGoalHeaderName && (jsonObject[i][scoreGoalHeaderName] || jsonObject[i][scoreGoalHeaderName] === 0)) {
            if (Number.isInteger(jsonObject[i][scoreGoal.columnName])) {
              atLeastOneScoreGoal = true
              break
            }
          }
        }
        // In this row we have at least one score goal, so we need to check both if they are all present and if we need to consider them as a change
        if (atLeastOneScoreGoal) {
          const scoreGridData: DraftScoreGridInput = {
            id: undefined,
            scoreGoals: [],
          }
          let currentScoreGoalValue: number
          const currentEmployeeData = employeeIdsAndScoreGoals.find((employeeData) => employeeData.employeeId === excelEmployeeId)!
          const currentScoreGoals: ScoreGoalDto[] | undefined | null = currentEmployeeData.scoreGoals
          let areScoreGoalsTheSame: boolean = currentEmployeeData.hasDraft
          for (const scoreGoal of scoreGoals) {
            const scoreGoalHeaderName: string | undefined = headerNames.find(
              (key: string) => key.toLocaleUpperCase() === scoreGoal.columnName.toLocaleUpperCase(),
            )
            if (!scoreGoalHeaderName || ['', null, undefined].includes(jsonObject[i][scoreGoalHeaderName])) {
              return t('draftScoreGrid.importModal.errors.missingScoreGoal', { name: scoreGoal.columnName, rowNo: i + 2 })
            }
            if (!Number.isInteger(jsonObject[i][scoreGoalHeaderName])) {
              return t('draftScoreGrid.importModal.errors.scoreGoalNotInteger', { name: scoreGoal.columnName, rowNo: i + 2 })
            }
            currentScoreGoalValue = parseInt(jsonObject[i][scoreGoalHeaderName])
            if (currentScoreGoalValue < 1 || currentScoreGoalValue > 10) {
              return t('draftScoreGrid.importModal.errors.invalidScoreGoalValue', {
                name: scoreGoal.columnName,
                value: currentScoreGoalValue,
                rowNo: i + 2,
              })
            }
            if (currentScoreGoals && areScoreGoalsTheSame) {
              areScoreGoalsTheSame =
                currentScoreGoalValue ===
                currentScoreGoals.find((scoreGoalData) => scoreGoalData.viewId === scoreGoal.type)!.resultEvaluation
            }
            scoreGridData.scoreGoals.push({
              viewId: scoreGoal.type,
              resultEvaluation: parseInt(jsonObject[i][scoreGoalHeaderName]),
            })
          }
          if (!areScoreGoalsTheSame) {
            res.push({
              employeeId: jsonObject[i][idHeaderName],
              scoreGrid: scoreGridData,
            })
          }
        }
      }
    }
    if (res.length === 0) {
      if (employeesNumberForSelectedBUs === 0) {
        return t('draftScoreGrid.importModal.errors.noEmployeeForSelectedBus')
      }
      return t('draftScoreGrid.importModal.errors.noChanges')
    }
    return res
  }

  const sendData = async () => {
    setIsImporting(true)
    setDropZoneProcessing({
      active: true,
      isProcessing: true,
      text: t('draftScoreGrid.importModal.processingMessage'),
    })
    importExcel(companyId, selectedBusinessUnits, country, importedData!)
      .then((res) => {
        if (res.errors && !!res.errors.length) {
          if (res.errors[0].extensions && res.errors[0].extensions.validationErrors.length > 0) {
            for (const error of res.errors[0].extensions.validationErrors) {
              if (error.field && error.field === 'importExcel' && error.message) {
                setDropZoneProcessing({
                  active: true,
                  isProcessing: false,
                  errorMessage: error.message,
                })
                return
              }
            }
          }
          if (res.errors[0].message) {
            setDropZoneProcessing({
              active: true,
              isProcessing: false,
              errorMessage: res.errors[0].message,
            })
          } else {
            setDropZoneProcessing({
              active: true,
              isProcessing: false,
              errorMessage: t('draftScoreGrid.errors.unknown'),
            })
          }
        } else {
          showNotification({
            type: 'success',
            message: t('draftScoreGrid.importModal.notifications.success.title'),
            description: t('draftScoreGrid.importModal.notifications.success.description'),
          })
          closeModal()
        }
      })
      .catch((err) => {
        setDropZoneProcessing({
          active: true,
          isProcessing: true,
          text: err,
        })
      })
      .finally(() => {
        setIsImporting(false)
      })
  }

  return (
    <PerModal
      size="small"
      title={{
        heading: t('draftScoreGrid.importModal.header'),
      }}
      visible={true}
      centered
      destroyOnClose={true}
      onCancel={closeModal}
      footer={{
        cancelBtn: {
          onClick: closeModal,
        },
        customBtns: [
          {
            text: t('draftScoreGrid.importModal.confirmButton'),
            onClick: sendData,
            color: 'success',
            icon: 'check_stroke',
            disabled:
              !importedData ||
              !dropZoneProcessing.active ||
              dropZoneProcessing.isProcessing ||
              dropZoneProcessing.errorMessage !== undefined,
            isProcessing: { active: isImporting },
          },
        ],
      }}
    >
      <PerText mb="xs" className="import-excel-modal-description">
        {t('draftScoreGrid.importModal.description')}
      </PerText>

      <PerText className="import-excel-modal-description">
        <Trans i18nKey="draftScoreGrid.importModal.description2" components={[<b></b>]} />
      </PerText>

      {businessUnitsOfCompany.length && (
        <PerFlexRow direction="row" align="middle">
          <div style={{ width: '30%' }}>
            <Controller
              name={`all.value`}
              control={control}
              render={({ field: { value } }) => (
                <>
                  <Checkbox
                    name={`all.value`}
                    checked={value}
                    onChange={() => {
                      const currentValue = getValues().all.value
                      setValue(`all.value`, !currentValue)
                      const businessUnits = getValues().businessUnits
                      for (let index = 0; index < businessUnits.length; index++) {
                        setValue(`businessUnits.${index}.value`, !currentValue)
                      }
                      setSelectedBusinessUnits([
                        ...getValues()
                          .businessUnits.filter((bu) => !!bu.value)
                          .map((bu) => bu.id),
                      ])
                    }}
                    disabled={!!importedData}
                  >
                    <PerText>{t('draftScoreGrid.importModal.all')}</PerText>
                  </Checkbox>
                  <PerSpacer mb="xs" />
                </>
              )}
            />
          </div>
          <div style={{ width: '20%' }} />

          {businessUnitsOfCompany.map((bu, index) => {
            return (
              <React.Fragment key={index}>
                <div style={{ width: '30%' }}>
                  <Controller
                    name={`businessUnits.${index}.value`}
                    control={control}
                    render={({ field: { value } }) => (
                      <>
                        <Checkbox
                          name={`businessUnits.${index}.value`}
                          checked={value}
                          onChange={() => {
                            const currentValue = getValues().businessUnits.find((b) => b.id === bu.id)!.value
                            setValue(`businessUnits.${index}.value`, !currentValue)
                            const allValue = getValues().businessUnits.every((bu) => bu.value)
                            setValue(`all.value`, allValue)
                            setSelectedBusinessUnits([
                              ...getValues()
                                .businessUnits.filter((bu) => !!bu.value)
                                .map((bu) => bu.id),
                            ])
                          }}
                          disabled={!!importedData}
                        >
                          <PerText>{bu.name}</PerText>
                        </Checkbox>
                        <PerSpacer mb="xs" />
                      </>
                    )}
                  />
                </div>
                <div style={{ width: '20%' }} />
              </React.Fragment>
            )
          })}
        </PerFlexRow>
      )}

      {(!dropZoneProcessing.active || dropZoneProcessing.errorMessage) && !!selectedBusinessUnits.length && (
        <PerFlexRow direction="row" align="middle">
          <PerDropzone
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onFileDropped={(file: unknown): void => {
              setDropZoneError(undefined)
              onDrop(file)
            }}
            maxAllowedSize={MAX_ALLOWED_FILE_SIZE}
            footerText={t('draftScoreGrid.importModal.formatMessage')}
            footerTextClassNames={['import-excel-modal-footer-text']}
            width="50%"
            errorText={dropZoneError}
            errorTextClassNames={['import-excel-modal-error-text']}
          />
        </PerFlexRow>
      )}
      {(dropZoneProcessing.active || dropZoneProcessing.isProcessing) && (
        <div ref={processingMessageRef}>
          <PerFlexRow align="middle" direction="row" justify="space-between" mt="s">
            <Col span={3}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '16px',
                  background: dropZoneProcessing.isProcessing ? '#FFFFFF' : dropZoneProcessing.errorMessage ? '#FFDAD4' : '#88D22A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Spin className="appraisal-teaser-info" indicator={antIcon} />
              </div>
            </Col>
            <Col span={21} style={{ padding: 0 }}>
              {dropZoneProcessing.errorMessage && (
                <PerFlexRow direction="column">
                  <PerText className="import-excel-modal-loading-success-message-text">
                    {t('draftScoreGrid.importModal.errors.message1')}
                  </PerText>
                  <PerText className="import-excel-modal-loading-success-message-text" mb="s">
                    {dropZoneProcessing.errorMessage}
                  </PerText>
                  <PerText className="import-excel-modal-loading-success-message-text">
                    {t('draftScoreGrid.importModal.errors.message2')}
                  </PerText>
                </PerFlexRow>
              )}
              {!dropZoneProcessing.errorMessage && (
                <PerFlexRow direction="column">
                  <PerFlexRow direction="row" align="middle">
                    <PerText className="import-excel-modal-loading-success-message-text">{dropZoneProcessing.text}</PerText>{' '}
                    <PerButtonCircle
                      ml="s"
                      icon="delete_fill"
                      color="error"
                      variant="pastel"
                      size="small"
                      onClick={() => {
                        deleteDroppedFile()
                      }}
                      tooltip={t('global.button.cancel')}
                      disabled={isImporting}
                    />
                  </PerFlexRow>
                  <PerText className="import-excel-modal-loading-footer">{filename}</PerText>
                </PerFlexRow>
              )}
            </Col>
          </PerFlexRow>
        </div>
      )}
    </PerModal>
  )
}
